import { z } from 'zod';
import Papa from 'papaparse';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

export const ContractResponseSchema = z.object({
  id: z.string(),
  title: z.string(),
  agency: z.string(),
  naicsCode: z.string(),
  setAside: z.string().optional(),
  status: z.enum(['active', 'inactive', 'archived']),
  value: z.number().optional(),
  postedDate: z.string(),
  deadlineDate: z.string(),
  description: z.string(),
  location: z.string().optional(),
  noticeId: z.string(),
  contractUrl: z.string(),
  isUrlValid: z.boolean().optional(),
});

export type ContractResponse = z.infer<typeof ContractResponseSchema>;

interface FetchContractsParams {
  status?: string;
  agency?: string;
  naicsCode?: string;
  setAside?: string;
  page?: number;
  limit?: number;
}

export class SamGovAPI {
  private apiKey: string;
  private baseUrl = 'https://api.sam.gov/opportunities/v2';
  private controller: AbortController | null = null;

  constructor(apiKey: string) {
    this.apiKey = apiKey;
  }

  private getHeaders(): HeadersInit {
    return {
      'Authorization': `Bearer ${this.apiKey}`,
      'api_key': this.apiKey,
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    };
  }

  public cancelCurrentRequest(): void {
    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }
  }

  public async fetchContracts(params: FetchContractsParams = {}): Promise<ContractResponse[]> {
    this.cancelCurrentRequest();
    this.controller = new AbortController();

    try {
      const queryParams = new URLSearchParams({
        api_key: this.apiKey,
        postedFrom: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        limit: (params.limit || 10).toString(),
        offset: (((params.page || 1) - 1) * (params.limit || 10)).toString(),
        ...params,
      });

      const response = await fetch(
        `${this.baseUrl}/search?${queryParams.toString()}`,
        {
          headers: this.getHeaders(),
          signal: this.controller.signal,
        }
      );

      if (!response.ok) {
        throw new Error(`SAM.gov API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data.opportunityList.map(this.mapContractResponse);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return [];
      }
      throw error;
    }
  }

  private mapContractResponse(item: any): ContractResponse {
    return {
      id: item.noticeId,
      title: item.title,
      agency: item.department,
      naicsCode: item.naicsCode,
      setAside: item.setAside,
      status: item.active ? 'active' : 'inactive',
      value: parseFloat(item.baseAndAllOptionsValue) || undefined,
      postedDate: item.postedDate,
      deadlineDate: item.responseDeadLine,
      description: item.description,
      location: item.placeOfPerformance,
      noticeId: item.noticeId,
      contractUrl: `https://sam.gov/opp/${item.noticeId}/view`,
      isUrlValid: true,
    };
  }

  public async exportContracts(contracts: ContractResponse[], format: 'csv' | 'pdf'): Promise<void> {
    if (contracts.length === 0) {
      throw new Error('No contracts to export');
    }

    try {
      if (format === 'csv') {
        this.exportToCSV(contracts);
      } else {
        this.exportToPDF(contracts);
      }
    } catch (error) {
      console.error('Export error:', error);
      throw new Error(`Failed to export contracts as ${format}`);
    }
  }

  private exportToCSV(contracts: ContractResponse[]): void {
    const csvData = Papa.unparse(contracts.map(contract => ({
      'Title': contract.title,
      'Agency': contract.agency,
      'NAICS Code': contract.naicsCode,
      'Set-Aside': contract.setAside || 'N/A',
      'Status': contract.status,
      'Value': contract.value ? `$${contract.value.toLocaleString()}` : 'N/A',
      'Posted Date': new Date(contract.postedDate).toLocaleDateString(),
      'Deadline': new Date(contract.deadlineDate).toLocaleDateString(),
      'Location': contract.location || 'N/A',
      'Notice ID': contract.noticeId,
      'URL': contract.contractUrl,
    })));

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `contracts_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  private exportToPDF(contracts: ContractResponse[]): void {
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(16);
    doc.text('Contracts Report', 14, 15);
    doc.setFontSize(10);
    doc.text(`Generated on ${new Date().toLocaleDateString()}`, 14, 22);

    // Create table
    const tableData = contracts.map(contract => [
      contract.title,
      contract.agency,
      contract.naicsCode,
      contract.setAside || 'N/A',
      contract.status,
      contract.value ? `$${contract.value.toLocaleString()}` : 'N/A',
      new Date(contract.deadlineDate).toLocaleDateString(),
    ]);

    // Add table
    (doc as any).autoTable({
      startY: 30,
      head: [['Title', 'Agency', 'NAICS', 'Set-Aside', 'Status', 'Value', 'Deadline']],
      body: tableData,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [102, 102, 255] },
    });

    // Save the PDF
    doc.save(`contracts_${new Date().toISOString().split('T')[0]}.pdf`);
  }

  public async testConnection(): Promise<boolean> {
    try {
      await this.fetchContracts({ limit: 1 });
      return true;
    } catch (error) {
      console.error('SAM.gov API connection test failed:', error);
      return false;
    }
  }

  public async importContracts(file: File): Promise<ContractResponse[]> {
    // This is a placeholder for the actual implementation
    // You would need to implement the logic to parse different file formats
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve([]);
      }, 1000);
    });
  }
}

export const samGovApi = new SamGovAPI(import.meta.env.VITE_SAM_GOV_API_KEY);